import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "patient-medication-calendar" }
const _hoisted_2 = { class: "medication-calendar__date-filter" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("label", { class: "medication-calendar__date-filter__label" }, " Дата приёма: ", -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("div", { class: "spacer" }, null, -1)
const _hoisted_5 = { class: "medication-calendar__medication-filter" }
const _hoisted_6 = /*#__PURE__*/_createElementVNode("label", { class: "medication-calendar__medication-filter__label" }, " МикроРНК: ", -1)
const _hoisted_7 = {
  key: 0,
  style: {"color":"#ccc"}
}
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { class: "medication-calendar__subtype-filter" }
const _hoisted_10 = /*#__PURE__*/_createElementVNode("label", { class: "medication-calendar__subtype-filter__label" }, " Подтипы: ", -1)

import {
    computed,
    ref,
    watch,
} from 'vue';

import {
    Button as AButton,
    DatePicker as ADatePicker,
    Select as ASelect,
    Spin as ASpin,
} from 'ant-design-vue';

import {
    keyBy,
    partition,
    Dictionary,
} from 'lodash';
import moment, { Moment } from 'moment';

import PlusOutlined from '@ant-design/icons-vue/PlusOutlined';
import RetweetOutlined from '@ant-design/icons-vue/RetweetOutlined';
import FileExcelOutlined from '@ant-design/icons-vue/FileExcelOutlined';

import {
    IMedication,
    IPatientMedicationCalendarItem,
    IIntakeForms,
} from '../../../life-impulse-tools/types';
import {
    getClinicMedication,
    getIntakeForms,
    getPatientMedicationCalendar,
    getPatientMedicationCalendarSelectedTypes,
    getPatientMedicationCourses,
    updatePatientMedicationCalendarSelectedTypes,
} from '../../api';

import {
    showErrorNotification,
    convertLayout,
    convertAlphabet,
    useRequest,
} from '../../utils';

import MedicationCalendarTable from './MedicationCalendarTable.vue';

interface PatientMedicationCalendarFilter {
    dateFrom?: Moment;
    dateTo?: Moment;
}
interface SelectOption {
    key: string;
    value: string;
    disabled: boolean;
    children: { el: HTMLElement }[];
}

export default /*#__PURE__*/_defineComponent({
  __name: 'PatientMedicationCalendar',
  props: {
    patientId: { default: undefined }
  },
  setup(__props: any) {

const props = __props as { patientId: number }



const dateFilter = ref<PatientMedicationCalendarFilter>({
    dateFrom: undefined,
    dateTo: undefined,
});
const calendarData = ref<IPatientMedicationCalendarItem[]>([]);
const isLoading = ref(false);
const intakeForms = ref<IIntakeForms[]>([]);
const allPatientMedicationMap = ref<Dictionary<IMedication>>({});
const availablePatientMedication = ref<IMedication[]>([]);
const selectedMedication = ref<number[]>([]);
const selectedSubtypes = ref<number[]>([]);

const availablePatientMedicationMap = computed(() => keyBy(availablePatientMedication.value, 'id'));
const selectedMedicationMap = computed(() => keyBy(selectedMedication.value));
const selectedSubtypesMap = computed(() => keyBy(selectedSubtypes.value));

const getMedicationName = (medication: { name: string; deleted?: number }) => `${
    medication.name}${medication.deleted ? ' (архив)' : ''}`;

const medicationWoSubtypes = computed(() => availablePatientMedication.value.filter(
    (medication) => {
        const { schemeId } = medication;
        return (schemeId === 1 || schemeId === 2) && (
            !selectedMedication.value.length
            || selectedMedicationMap.value[medication.id]
        );
    },
));

const medicationWithSubtypes = computed(() => availablePatientMedication.value.filter(
    (medication) => {
        const { schemeId, subtypes } = medication;
        return schemeId !== 1 && subtypes?.length && (
            !selectedMedication.value.length
            || selectedMedicationMap.value[medication.id]
        );
    },
).map((medication) => ({
    ...medication,
    id: `group_${medication.id}`,
})));

const isFromDateDisabled = (date: Moment) => {
    const { dateTo } = dateFilter.value;
    return !!(dateTo && date > moment(dateTo).endOf('day'));
};

const isToDateDisabled = (date: Moment) => {
    const { dateFrom } = dateFilter.value;
    return !!(dateFrom && date < moment(dateFrom).startOf('day'));
};

const filterSelectOptions = (input: string, option: SelectOption) => {
    const text = option.children?.[0]?.el?.innerText;
    if (!text) {
        return false;
    }
    return text.toLowerCase().includes(input.toLowerCase())
        || text.toLowerCase().includes(convertLayout(input).toLowerCase())
        || text.toLowerCase().includes(convertAlphabet(input).toLowerCase());
};

const medicationFilter = computed(() => {
    const [clinicMedicationIds, medicationSubtypeIds] = partition(
        selectedSubtypes.value,
        (id) => !!availablePatientMedicationMap.value[id],
    );
    return {
        clinicMedicationIds,
        medicationSubtypeIds,
    };
});

const fetchPatientMedication = async () => {
    if (!props.patientId) {
        return;
    }
    const { dateFrom, dateTo } = dateFilter.value;
    const { clinicMedicationIds, medicationSubtypeIds } = medicationFilter.value;
    if (!clinicMedicationIds.length && !medicationSubtypeIds.length) {
        calendarData.value = [];
        return;
    }

    const filter = {
        clinicMedicationIds,
        medicationSubtypeIds,
        dateFrom: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
        dateTo: dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
    };

    isLoading.value = true;
    const response = await getPatientMedicationCalendar(props.patientId, filter);
    isLoading.value = false;
    if (typeof response === 'string') {
        showErrorNotification(response);
        return;
    }
    calendarData.value = response;
};

const saveSelectedSubtypes = (ids: number[]) => {
    updatePatientMedicationCalendarSelectedTypes(
        props.patientId,
        ids,
    ).catch(() => {});
};

const onSelectedSubtypesChange = (ids: number[]) => {
    saveSelectedSubtypes(ids);
    fetchPatientMedication();
};

const addMedication = () => {
    const sourceList = selectedMedication.value.length
        ? selectedMedication.value
        : availablePatientMedication.value.map(({ id }) => id);

    selectedSubtypes.value = selectedSubtypes.value.concat(
        sourceList.flatMap((medicationId) => {
            const medication = availablePatientMedicationMap.value[medicationId];
            const result = [];
            if (medication.schemeId === 1) {
                return !selectedSubtypesMap.value[medicationId] ? [medicationId] : [];
            }
            if (medication.schemeId === 2
                && !selectedSubtypesMap.value[medicationId]
            ) {
                result.push(medicationId);
            }
            return [
                ...result,
                ...medication.subtypes!.filter(
                    (subtype) => !selectedSubtypesMap.value[subtype.id],
                ).map(({ id }) => id),
            ];
        }),
    );
    saveSelectedSubtypes(selectedSubtypes.value);
    fetchPatientMedication();
};

const replaceMedication = () => {
    const sourceList = selectedMedication.value.length
        ? selectedMedication.value
        : availablePatientMedication.value.map(({ id }) => id);
    selectedSubtypes.value = sourceList.flatMap((medicationId) => {
        const medication = availablePatientMedicationMap.value[medicationId];
        if (medication.schemeId === 1) {
            return [medicationId];
        }

        const result = [];
        if (medication.schemeId === 2) {
            result.push(medicationId);
        }
        return [
            ...result,
            ...medication.subtypes!.map(({ id }) => id),
        ];
    });
    saveSelectedSubtypes(selectedSubtypes.value);
    fetchPatientMedication();
};

const exportData = () => {
    if (!props.patientId || !calendarData.value.length) {
        return;
    }

    const { dateFrom, dateTo } = dateFilter.value;
    const { clinicMedicationIds, medicationSubtypeIds } = medicationFilter.value;
    let url = `/api/patient/${
        props.patientId
    }/medication/calendar/export?clinicMedicationIds=${
        JSON.stringify(clinicMedicationIds)
    }&medicationSubtypeIds=${
        JSON.stringify(medicationSubtypeIds)
    }`;
    if (dateFrom) {
        url += `&dateFrom=${dateFrom.format('YYYY-MM-DD')}`;
    }
    if (dateTo) {
        url += `&dateTo=${dateTo.format('YYYY-MM-DD')}`;
    }
    window.open(url);
};

const getIntakeFormsData = async () => {
    let response;
    try {
        response = await getIntakeForms();
    } catch (err) {
        showErrorNotification('Не удалось загрузить справочные данные.');
        return;
    }
    intakeForms.value = response.data;
};
getIntakeFormsData();

const siftMedication = (
    patientMedication: IMedication[],
    currentMedicationMap: Dictionary<IMedication>,
) => {
    const result: IMedication[] = [];
    const map: Record<number, IMedication> = {};
    patientMedication.forEach((medication) => {
        const { id, currentId, subtypes } = medication;
        const isArchived = id !== currentId;
        if (!map[currentId]) {
            map[currentId] = medication;
            if (isArchived) {
                // eslint-disable-next-line no-param-reassign
                medication.name = currentMedicationMap[currentId].name;
            }
            result.push(medication);
            return;
        }

        const knownSubtypesMap = keyBy(map[currentId].subtypes, 'name');
        const moreSubtypes = subtypes!.filter(
            (subtype) => !knownSubtypesMap[subtype.name],
        );
        map[currentId].subtypes = [
            ...map[currentId].subtypes!,
            ...moreSubtypes,
        ];
    });
    return result;
};

const [{
    data: patientMedicationCourses,
}, doPatientMedicationCoursesRequest] = useRequest({
    fetcher: getPatientMedicationCourses,
    defaultErrorMsg: 'Не удалось загрузить курсы приёмов пациента.',
});
const patientMedicationCoursesMap = computed(
    () => keyBy(patientMedicationCourses.value || [], 'id'),
);

watch(() => props.patientId, async (patientId) => {
    if (!patientId) {
        return;
    }

    doPatientMedicationCoursesRequest(patientId);

    const response = await getClinicMedication({
        patientId,
        archived: true,
        deleted: true,
    });
    if (typeof response === 'string') {
        showErrorNotification(response);
        return;
    }

    const currentMedicationResponse = await getClinicMedication({
        ids: response.filter(
            ({ id, currentId }) => id !== currentId,
        ).map(
            ({ currentId }) => currentId,
        ),
        deleted: true,
    });
    if (typeof currentMedicationResponse === 'string') {
        showErrorNotification(currentMedicationResponse);
        return;
    }

    allPatientMedicationMap.value = keyBy(response, 'id');
    availablePatientMedication.value = siftMedication(
        response,
        keyBy(currentMedicationResponse, 'id'),
    );

    const lsResponse = await getPatientMedicationCalendarSelectedTypes(patientId);
    if (typeof lsResponse === 'string') {
        return;
    }
    selectedSubtypes.value = lsResponse;
    fetchPatientMedication();
}, {
    immediate: true,
});

return (_ctx: any,_cache: any) => {
  const _component_a_select_option = _resolveComponent("a-select-option")!
  const _component_a_select_opt_group = _resolveComponent("a-select-opt-group")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(ASpin), {
      delay: 200,
      spinning: isLoading.value,
      tip: "Загрузка..."
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _hoisted_3,
          _createVNode(_unref(ADatePicker), {
            value: dateFilter.value.dateFrom,
            "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((dateFilter.value.dateFrom) = $event)),
            "data-test-id": "date-from",
            placeholder: "С даты...",
            mode: "date",
            format: "DD.MM.YYYY",
            "disabled-date": isFromDateDisabled,
            onChange: fetchPatientMedication
          }, null, 8, ["value"]),
          _createVNode(_unref(ADatePicker), {
            value: dateFilter.value.dateTo,
            "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((dateFilter.value.dateTo) = $event)),
            "data-test-id": "date-to",
            placeholder: "По дату...",
            format: "DD.MM.YYYY",
            "disabled-date": isToDateDisabled,
            onChange: fetchPatientMedication
          }, null, 8, ["value"]),
          _hoisted_4,
          _createVNode(_unref(AButton), {
            "data-test-id": "export-data",
            type: "primary",
            disabled: !calendarData.value.length,
            onClick: exportData
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(FileExcelOutlined))
            ]),
            default: _withCtx(() => [
              _createTextVNode(" Экспорт ")
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _hoisted_6,
          _createVNode(_unref(ASelect), {
            value: selectedMedication.value,
            "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((selectedMedication).value = $event)),
            "data-test-id": "selected-medication",
            mode: "multiple",
            "allow-clear": "",
            "show-search": "",
            placeholder: "Укажите один или несколько...",
            "dropdown-match-select-width": false,
            "filter-option": filterSelectOptions,
            class: "medication-calendar__medication-filter__input"
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availablePatientMedication.value, (medication) => {
                return (_openBlock(), _createBlock(_component_a_select_option, {
                  key: medication.id,
                  "data-test-id": "selected-medication-option"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", null, [
                      (medication.code)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_7, " (" + _toDisplayString(medication.code) + ") ", 1))
                        : _createCommentVNode("", true),
                      _createTextVNode(" " + _toDisplayString(medication.name) + " ", 1),
                      (medication.deleted)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_8, " (архив) "))
                        : _createCommentVNode("", true)
                    ])
                  ]),
                  _: 2
                }, 1024))
              }), 128))
            ]),
            _: 1
          }, 8, ["value"]),
          _createVNode(_unref(AButton), {
            "data-test-id": "add-medication",
            type: "primary",
            onClick: addMedication
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(PlusOutlined))
            ]),
            default: _withCtx(() => [
              _createTextVNode(" Добавить ")
            ]),
            _: 1
          })
        ]),
        _createElementVNode("div", _hoisted_9, [
          _hoisted_10,
          _createVNode(_unref(ASelect), {
            value: selectedSubtypes.value,
            "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((selectedSubtypes).value = $event)),
            "data-test-id": "selected-subtypes",
            mode: "multiple",
            "allow-clear": "",
            "show-search": "",
            placeholder: "Укажите препараты/подтипы...",
            "dropdown-match-select-width": false,
            "filter-option": filterSelectOptions,
            class: "medication-calendar__subtype-filter__input",
            onChange: onSelectedSubtypesChange
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(medicationWoSubtypes), (medication) => {
                return (_openBlock(), _createBlock(_component_a_select_option, {
                  key: medication.id,
                  "data-test-id": "selected-subtypes-option"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(getMedicationName(medication)), 1)
                  ]),
                  _: 2
                }, 1024))
              }), 128)),
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(medicationWithSubtypes), (medication) => {
                return (_openBlock(), _createBlock(_component_a_select_opt_group, {
                  key: medication.id,
                  label: getMedicationName(medication)
                }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(medication.subtypes, (subtype) => {
                      return (_openBlock(), _createBlock(_component_a_select_option, {
                        key: subtype.id,
                        "data-test-id": "selected-subtypes-group-option"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(getMedicationName(subtype)), 1)
                        ]),
                        _: 2
                      }, 1024))
                    }), 128))
                  ]),
                  _: 2
                }, 1032, ["label"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["value"]),
          _createVNode(_unref(AButton), {
            "data-test-id": "replace-medication",
            type: "primary",
            onClick: replaceMedication
          }, {
            icon: _withCtx(() => [
              _createVNode(_unref(RetweetOutlined))
            ]),
            default: _withCtx(() => [
              _createTextVNode(" Заменить ")
            ]),
            _: 1
          })
        ])
      ]),
      _: 1
    }, 8, ["spinning"]),
    _createVNode(MedicationCalendarTable, {
      "calendar-data": calendarData.value,
      "intake-forms": intakeForms.value,
      "medication-map": allPatientMedicationMap.value,
      "medication-courses-map": _unref(patientMedicationCoursesMap)
    }, null, 8, ["calendar-data", "intake-forms", "medication-map", "medication-courses-map"])
  ]))
}
}

})